import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import {
  DEFAULT_FINGERING_DISPLAY,
  FINGERING_DISPLAY_LEVELS,
  type FingeringDisplay,
} from 'Fingering/display';
import { setKeySignatureDisplay } from 'NotationDisplay/keySignatureDisplaySlice';
import { normalizeKeySignatureDisplay } from 'NotationDisplay/keySignatureDisplaySettings';
import { setScaleFingerNumbers } from 'NotationDisplay/scaleFingerNumbersSlice';
import { normalizeFingerNumbersDisplay } from 'NotationDisplay/scaleFingerNumbersSettings';
import {
  DEFAULT_FINGER_NUMBERS_DISPLAY,
  FINGER_NUMBERS_DISPLAYS,
  type FingerNumbersDisplay,
} from 'Seeds/ExerciseContent/scaleFingering';
import {
  DEFAULT_KEY_SIGNATURE_DISPLAY,
  KEY_SIGNATURE_DISPLAYS,
  type KeySignatureDisplay,
} from 'Seeds/ExerciseContent/scaleNotation';
import type { ErrorRecognitionActive } from 'Types';
import { PERFECT_PHRASE_DEFAULT_COUNT } from 'Types/RepertoireTypes';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import {
  SCORE_THEMES,
  normalizeScoreTheme,
  scoreThemeToPracticeDarkMode,
  type ScoreTheme,
} from 'Utils/ScoreTheme';
import type { SettingDefinition, SettingSurface, StoreDescriptor } from './settingTypes';

/**
 * Every setting the app has today (web and the native apps run the same code),
 * one entry each: what it is, how far it reaches, where it is shown, which
 * precedence rule decides it, and where today's code keeps it. Storage stays
 * where it is: an entry describes today's stores and, where it can, binds the
 * model to them through the existing actions.
 *
 * Not settings, so not here: developer and debug switches, feature flags,
 * audio-diagnostics panels, editor panel state, the notification switches on the
 * Settings page (placeholders that store nothing), audio calibration (an action
 * and its saved profile), and transient play state.
 */

const defineSetting = <T>(definition: SettingDefinition<T>): SettingDefinition<T> => definition;

const booleanOr =
  (fallback: boolean) =>
  (raw: unknown): boolean =>
    typeof raw === 'boolean' ? raw : fallback;

const numberIn =
  (min: number, max: number, fallback: number) =>
  (raw: unknown): number =>
    typeof raw === 'number' && Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : fallback;

const positiveWholeNumberOr =
  (fallback: number) =>
  (raw: unknown): number =>
    typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.round(raw) : fallback;

const oneOf =
  <T extends string>(options: readonly T[], fallback: T) =>
  (raw: unknown): T =>
    typeof raw === 'string' && options.includes(raw as T) ? (raw as T) : fallback;

// ---------------------------------------------------------------------------
// Sound and input (global)
// ---------------------------------------------------------------------------

const keyboardSound = defineSetting<boolean>({
  key: 'keyboardSound',
  label: 'Keyboard Sound',
  valueType: { kind: 'boolean' },
  defaultValue: true,
  parse: booleanOr(true),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_practice', 'sight_reading_cog', 'repertoire_cog', 'onboarding_sound'],
  stores: [
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.audio_on (mainAppReducer.userData.audio_on)',
    },
  ],
  bindings: {
    global: {
      select: (state) => state.mainAppReducer?.userData?.audio_on,
      write: (value) => appActions.setAudioOn({ audioOn: value }),
    },
  },
  routed: false,
  notes:
    'The switch between keyboard speakers and device speakers: one setting, the piano playback producer.',
});

const metronomeSound = defineSetting<boolean>({
  key: 'metronomeSound',
  label: 'Metronome Sound',
  valueType: { kind: 'boolean' },
  defaultValue: true,
  parse: booleanOr(true),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['sight_reading_cog', 'repertoire_cog'],
  stores: [
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.rep_metronome_sound (mainAppReducer.userData.rep_metronome_sound)',
    },
  ],
  bindings: {
    global: {
      select: (state) => state.mainAppReducer?.userData?.rep_metronome_sound,
      write: (value) => appActions.setRepMetronomeSound(value),
    },
  },
  routed: false,
  notes:
    'The column carries a repertoire prefix but sight reading reads it too. The sight-reading cog also switches the running metronome when it writes; a write through the model changes the stored value only.',
});

const accentDownbeats = defineSetting<boolean>({
  key: 'accentDownbeats',
  label: 'Accent Downbeats',
  valueType: { kind: 'boolean' },
  defaultValue: true,
  parse: booleanOr(true),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['repertoire_cog'],
  stores: [
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.rep_downbeats_sound (mainAppReducer.userData.rep_downbeats_sound)',
    },
  ],
  bindings: {
    global: {
      select: (state) => state.mainAppReducer?.userData?.rep_downbeats_sound,
      write: (value) => appActions.setRepDownbeatsSound(value),
    },
  },
  routed: false,
});

const preferredInput = defineSetting<'midi' | 'audio'>({
  key: 'preferredInput',
  label: 'Preferred Input',
  valueType: { kind: 'enum', options: ['midi', 'audio'] },
  defaultValue: 'midi',
  parse: oneOf<'midi' | 'audio'>(['midi', 'audio'], 'midi'),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_practice'],
  featureFlag: 'USE_AUDIO_RECOGNITION',
  stores: [
    {
      layer: 'global',
      kind: 'device_storage',
      location: 'preferred-input-mode-v1 and preferred-input-mode-explicit-v1',
    },
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.prefer_audio_recognition_input',
    },
  ],
  routed: false,
  notes:
    'Held by the MIDI context, which reconciles the device keys with the server column; not in Redux, so the model does not bind it.',
});

// Volume: four channels, each with its own level and its own mute, kept per
// user on the device in one record (`<user id>-museflow_volume_settings`).
const VOLUME_SURFACES: readonly SettingSurface[] = [
  'settings_practice',
  'sight_reading_cog',
  'repertoire_cog',
  'tutorial_video',
];

const VOLUME_NOTES =
  'The Volume module (MUS-87) is on the release branch and reaches main when that release merges back; the model binds and routes these then. One stored record holds all eight values.';

const volumeStore = (field: string): StoreDescriptor => ({
  layer: 'global',
  kind: 'device_storage_per_user',
  location: `<user id>-museflow_volume_settings, field ${field}`,
});

const channelVolume = (channel: string, label: string): SettingDefinition<number> =>
  defineSetting<number>({
    key: `${channel}Volume`,
    label: `${label} volume`,
    valueType: { kind: 'number', min: 0, max: 100, step: 1 },
    defaultValue: 100,
    parse: (raw) => Math.round(numberIn(0, 100, 100)(raw)),
    scope: 'global',
    precedence: 'feel',
    surfaces: VOLUME_SURFACES,
    stores: [volumeStore(`${channel}.level`)],
    routed: false,
    notes: VOLUME_NOTES,
  });

const channelMuted = (channel: string, label: string): SettingDefinition<boolean> =>
  defineSetting<boolean>({
    key: `${channel}Muted`,
    label: `${label} mute`,
    valueType: { kind: 'boolean' },
    defaultValue: false,
    parse: booleanOr(false),
    scope: 'global',
    precedence: 'feel',
    surfaces: VOLUME_SURFACES,
    stores: [volumeStore(`${channel}.muted`)],
    routed: false,
    notes: VOLUME_NOTES,
  });

// ---------------------------------------------------------------------------
// Look of the score (global)
// ---------------------------------------------------------------------------

const scoreTheme = defineSetting<ScoreTheme>({
  key: 'scoreTheme',
  label: 'Score Theme',
  valueType: { kind: 'enum', options: SCORE_THEMES },
  defaultValue: 'light',
  parse: (raw) => normalizeScoreTheme(typeof raw === 'string' ? raw : undefined),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_practice'],
  stores: [
    { layer: 'global', kind: 'device_storage', location: 'museflow_score_theme' },
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.practice_score_theme (+ legacy practice_dark_mode)',
    },
  ],
  bindings: {
    global: {
      // The reducer folds the device's saved theme into userData, so this is the theme in force.
      select: (state) => state.mainAppReducer?.userData?.practice_score_theme,
      write: (value) =>
        appActions.updateUserData({
          practice_score_theme: value,
          practice_dark_mode: scoreThemeToPracticeDarkMode(value),
        }),
    },
  },
  routed: false,
  notes: "The device's saved theme wins over the server column when both exist.",
});

// Plain strings (the stored enum values), so loading the registry reads nothing
// from the constants module; the enum is only touched when a value is written.
const COLORBLIND_MODES = ['off', 'FULL', 'RED_GREEN'] as const;
export type ColorblindMode = (typeof COLORBLIND_MODES)[number];

const toColorblindType = (mode: ColorblindMode): COLORBLIND_TYPE | null =>
  mode === 'off' ? null : COLORBLIND_TYPE[mode];

const colorblindMode = defineSetting<ColorblindMode>({
  key: 'colorblindMode',
  label: 'Colorblind mode',
  valueType: { kind: 'enum', options: COLORBLIND_MODES },
  defaultValue: 'off',
  parse: oneOf<ColorblindMode>(COLORBLIND_MODES, 'off'),
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_accessibility'],
  stores: [
    {
      layer: 'global',
      kind: 'server_user_data',
      location: 'user_data.colorblind_type (null = off)',
    },
  ],
  bindings: {
    global: {
      select: (state) => {
        const stored = state.mainAppReducer?.userData?.colorblind_type;
        if (stored === undefined) return undefined;
        return stored ?? 'off';
      },
      write: (value) => appActions.setColorblindType(toColorblindType(value)),
    },
  },
  routed: false,
});

const keySignatureDisplay = defineSetting<KeySignatureDisplay>({
  key: 'keySignatureDisplay',
  label: 'Key signature',
  valueType: { kind: 'enum', options: KEY_SIGNATURE_DISPLAYS },
  defaultValue: DEFAULT_KEY_SIGNATURE_DISPLAY,
  parse: normalizeKeySignatureDisplay,
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_practice'],
  featureFlag: 'USE_EXERCISES',
  stores: [
    {
      layer: 'global',
      kind: 'device_storage_per_user',
      location: '<user id>-museflow_key_signature_display (keySignatureDisplayReducer.display)',
    },
  ],
  bindings: {
    global: {
      select: (state) => state.keySignatureDisplayReducer?.display,
      write: (value) => setKeySignatureDisplay(value),
    },
  },
  routed: true,
  notes:
    'A display fact, not an assist: it changes how Exercises write scales, never what is graded. Kept per user per device and through sign-out.',
});

const scaleFingerNumbers = defineSetting<FingerNumbersDisplay>({
  key: 'scaleFingerNumbers',
  label: 'Scale finger numbers',
  valueType: { kind: 'enum', options: FINGER_NUMBERS_DISPLAYS },
  defaultValue: DEFAULT_FINGER_NUMBERS_DISPLAY,
  parse: normalizeFingerNumbersDisplay,
  scope: 'global',
  precedence: 'feel',
  surfaces: ['settings_practice'],
  featureFlag: 'USE_EXERCISES',
  stores: [
    {
      layer: 'global',
      kind: 'device_storage_per_user',
      location: '<user id>-museflow_scale_finger_numbers (scaleFingerNumbersReducer.display)',
    },
  ],
  bindings: {
    global: {
      select: (state) => state.scaleFingerNumbersReducer?.display,
      write: (value) => setScaleFingerNumbers(value),
    },
  },
  routed: true,
  notes:
    'The reference fingering on every note of a scale run in Exercises (show by default). Display only: fingering is never graded, so it is not an assist. The full source + dial arrives with Settings 2.0.',
});

// ---------------------------------------------------------------------------
// Play-screen display (cog)
// ---------------------------------------------------------------------------

const cursor = defineSetting<boolean>({
  key: 'cursor',
  label: 'Cursor',
  valueType: { kind: 'boolean' },
  defaultValue: true,
  parse: booleanOr(true),
  scope: 'mode',
  modes: ['sight_reading', 'repertoire'],
  targetScope: 'global',
  precedence: 'feel',
  surfaces: ['sight_reading_cog', 'repertoire_cog'],
  stores: [
    {
      layer: 'mode',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.sightReadingCursorVisible',
    },
    {
      layer: 'mode',
      mode: 'repertoire',
      kind: 'device_storage',
      location: 'museflow_cursor_visible (repertoireReducer.cursorVisible)',
    },
  ],
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.sightReadingCursorVisible,
        write: (value) => appActions.setSightReadingCursorVisible(value),
      },
    },
  },
  routed: false,
  notes:
    "Two values today, one per mode; ruled one global value. Repertoire's toggle writes its own device key beside Redux, so the model binds sight reading only.",
});

const zoom = defineSetting<number>({
  key: 'zoom',
  label: 'Zoom Level',
  valueType: { kind: 'number', min: 0.5, max: 1, step: 0.05 },
  defaultValue: 0.9,
  parse: numberIn(0.5, 1, 0.9),
  scope: 'global',
  targetScope: 'mode',
  precedence: 'feel',
  surfaces: ['sight_reading_cog', 'repertoire_cog'],
  stores: [{ layer: 'global', kind: 'redux_persist', location: 'mainAppReducer.zoomLevel' }],
  bindings: {
    global: {
      select: (state) => state.mainAppReducer?.zoomLevel,
      write: (value) => appActions.setZoomLevel(value),
    },
  },
  routed: false,
  notes:
    'Screens also send a new zoom to the score scene when they change it; a write through the model changes the stored value only. Ruled a global default with a per-mode override.',
});

const fingerNumbers = defineSetting<FingeringDisplay>({
  key: 'fingerNumbers',
  label: 'Finger numbers',
  valueType: { kind: 'enum', options: FINGERING_DISPLAY_LEVELS },
  defaultValue: DEFAULT_FINGERING_DISPLAY,
  parse: oneOf<FingeringDisplay>(FINGERING_DISPLAY_LEVELS, DEFAULT_FINGERING_DISPLAY),
  scope: 'mode',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: ['sight_reading_cog'],
  featureFlag: 'USE_ERGOBOT_FINGERING',
  stores: [
    {
      layer: 'mode',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.sightReadingFingeringDisplay',
    },
  ],
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.sightReadingFingeringDisplay,
        write: (value) => appActions.setSightReadingFingeringDisplay(value),
      },
    },
  },
  routed: false,
  notes: 'Display only; never graded.',
});

const MAX_TRANSPOSE_SEMITONES = 7;

const transpose = defineSetting<number>({
  key: 'transpose',
  label: 'Transpose',
  valueType: {
    kind: 'number',
    min: -MAX_TRANSPOSE_SEMITONES,
    max: MAX_TRANSPOSE_SEMITONES,
    step: 1,
  },
  defaultValue: 0,
  parse: (raw) => Math.round(numberIn(-MAX_TRANSPOSE_SEMITONES, MAX_TRANSPOSE_SEMITONES, 0)(raw)),
  scope: 'instance',
  modes: ['sight_reading', 'repertoire'],
  precedence: 'feel',
  surfaces: ['sight_reading_cog', 'repertoire_cog'],
  stores: [
    {
      layer: 'mode',
      mode: 'sight_reading',
      kind: 'server_user_data',
      location: 'user_data.sight_reading_transposition',
    },
    {
      layer: 'instance',
      mode: 'repertoire',
      kind: 'session',
      location: 'RepertoirePlay transposeSemitones (one piece, one visit)',
    },
  ],
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.userData?.sight_reading_transposition,
        write: (value) => appActions.updateUserData({ sight_reading_transposition: value }),
      },
    },
  },
  routed: false,
  notes:
    'Changes the notes, so a clear is stamped with it. Sight reading keeps one value across sessions (in effect its mode default); ruled to apply to one session or piece.',
});

const SCALE_MODES: readonly ParallelModeConversion[] = [
  'off',
  'to_major',
  'to_minor',
  'to_harmonic_minor',
  'to_melodic_minor',
  'to_dorian',
  'to_mixolydian',
];

const scaleMode = defineSetting<ParallelModeConversion>({
  key: 'scaleMode',
  label: 'Scale / Mode',
  valueType: { kind: 'enum', options: SCALE_MODES },
  defaultValue: 'off',
  parse: oneOf<ParallelModeConversion>(SCALE_MODES, 'off'),
  scope: 'instance',
  modes: ['sight_reading', 'repertoire'],
  precedence: 'feel',
  surfaces: ['sight_reading_cog', 'repertoire_cog'],
  stores: [
    {
      layer: 'instance',
      mode: 'sight_reading',
      kind: 'session',
      location: 'Lesson sightReadingModeConversion',
    },
    {
      layer: 'instance',
      mode: 'repertoire',
      kind: 'session',
      location: 'RepertoirePlay modeConversion',
    },
  ],
  routed: false,
  notes: 'Changes the notes, so a clear is stamped with it. Screen state only today.',
});

// ---------------------------------------------------------------------------
// Repertoire practice
// ---------------------------------------------------------------------------

const REPERTOIRE_PLAYBACK_MODES = ['practice', 'listen'] as const;

const repertoirePlaybackMode = defineSetting<'practice' | 'listen'>({
  key: 'repertoirePlaybackMode',
  label: 'Accuracy Scoring',
  valueType: { kind: 'enum', options: REPERTOIRE_PLAYBACK_MODES },
  defaultValue: 'practice',
  parse: oneOf<'practice' | 'listen'>(REPERTOIRE_PLAYBACK_MODES, 'practice'),
  scope: 'mode',
  modes: ['repertoire'],
  precedence: 'feel',
  surfaces: ['repertoire_screen'],
  featureFlag: 'USE_REPERTOIRE_PLAYBACK_MODES',
  stores: [
    {
      layer: 'mode',
      mode: 'repertoire',
      kind: 'device_storage',
      location: 'museflow_repertoire_playback_mode',
    },
  ],
  routed: false,
  notes: 'Read and written by the repertoire playback helpers directly; not in Redux.',
});

const hands = defineSetting<ErrorRecognitionActive>({
  key: 'hands',
  label: 'Hands',
  valueType: { kind: 'hands' },
  defaultValue: { left: true, right: true },
  parse: (raw) => {
    const record = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
    return {
      left: typeof record.left === 'boolean' ? record.left : true,
      right: typeof record.right === 'boolean' ? record.right : true,
    };
  },
  scope: 'mode',
  modes: ['repertoire'],
  precedence: 'feel',
  surfaces: ['repertoire_screen'],
  stores: [
    {
      layer: 'mode',
      mode: 'repertoire',
      kind: 'redux_persist',
      location: 'mainAppReducer.errorRecognitionActive',
    },
  ],
  bindings: {
    mode: {
      repertoire: {
        select: (state) => state.mainAppReducer?.errorRecognitionActive,
        write: (value) => appActions.setErrorRecognitionActive(value),
      },
    },
  },
  routed: false,
  notes:
    'Which hands are graded. Reset to both hands each time the Repertoire page opens, so in practice it lasts one visit.',
});

const perfectPhrase = defineSetting<boolean>({
  key: 'perfectPhrase',
  label: 'Perfect Phrase',
  valueType: { kind: 'boolean' },
  defaultValue: false,
  parse: booleanOr(false),
  scope: 'mode',
  modes: ['repertoire'],
  precedence: 'feel',
  surfaces: ['repertoire_screen'],
  stores: [
    {
      layer: 'mode',
      mode: 'repertoire',
      kind: 'redux_persist',
      location: 'repertoireReducer.perfectPhraseEnabled',
    },
  ],
  bindings: {
    mode: {
      repertoire: {
        select: (state) => state.repertoireReducer?.perfectPhraseEnabled,
        write: (value) => repertoireActions.setPerfectPhraseEnabled(value),
      },
    },
  },
  routed: false,
});

const perfectPhraseCount = defineSetting<number>({
  key: 'perfectPhraseCount',
  label: 'Perfect Phrase count',
  valueType: { kind: 'number', min: 2, max: 4, step: 1 },
  defaultValue: PERFECT_PHRASE_DEFAULT_COUNT,
  parse: (raw) => Math.round(numberIn(2, 4, PERFECT_PHRASE_DEFAULT_COUNT)(raw)),
  scope: 'mode',
  modes: ['repertoire'],
  precedence: 'feel',
  surfaces: ['repertoire_screen'],
  stores: [
    {
      layer: 'mode',
      mode: 'repertoire',
      kind: 'redux_persist',
      location: 'repertoireReducer.perfectPhraseCount',
    },
  ],
  bindings: {
    mode: {
      repertoire: {
        select: (state) => state.repertoireReducer?.perfectPhraseCount,
        write: (value) => repertoireActions.setPerfectPhraseCount(value),
      },
    },
  },
  routed: false,
});

// ---------------------------------------------------------------------------
// Sight-reading tempo and auto-tempo
// ---------------------------------------------------------------------------

const ataStore = (column: string, reduxField: string): StoreDescriptor[] => [
  {
    layer: 'mode',
    mode: 'sight_reading',
    kind: 'server_ata_settings',
    location: `user_ata_settings.${column} (mainAppReducer.ataSettings.${reduxField})`,
  },
];

const autoTempo = defineSetting<boolean>({
  key: 'autoTempo',
  label: 'Auto Tempo',
  valueType: { kind: 'boolean' },
  defaultValue: true,
  parse: booleanOr(true),
  scope: 'mode',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: ['sight_reading_tempo'],
  stores: [
    {
      layer: 'mode',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.ataSettings.ataEnabled',
    },
  ],
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.ataSettings?.ataEnabled,
        write: (value) => appActions.setAutoTempoAdjustment(value),
      },
    },
  },
  routed: false,
});

const customAutoTempo = defineSetting<boolean>({
  key: 'customAutoTempo',
  label: 'Use my auto-tempo settings',
  valueType: { kind: 'boolean' },
  defaultValue: false,
  parse: booleanOr(false),
  scope: 'mode',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: [],
  stores: ataStore('using_custom_settings', 'usingCustomSettings'),
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.ataSettings?.usingCustomSettings,
        write: (value) => appActions.setCustomAtaSettings({ usingCustomSettings: value }),
      },
    },
  },
  routed: false,
  notes: 'Stored, loaded and saved, but no screen sets it today.',
});

type AtaNumberField = 'customIncThreshold' | 'customDecThreshold' | 'customIncAmt' | 'customDecAmt';

const ataNumber = (
  key: string,
  label: string,
  field: AtaNumberField,
  column: string,
  range: { min: number; max: number; fallback: number },
): SettingDefinition<number> =>
  defineSetting<number>({
    key,
    label,
    valueType: { kind: 'number', min: range.min, max: range.max, step: 1 },
    defaultValue: range.fallback,
    parse: (raw) => Math.round(numberIn(range.min, range.max, range.fallback)(raw)),
    scope: 'mode',
    modes: ['sight_reading'],
    precedence: 'feel',
    surfaces: ['sight_reading_tempo'],
    stores: ataStore(column, field),
    bindings: {
      mode: {
        sight_reading: {
          select: (state) => state.mainAppReducer?.ataSettings?.[field],
          write: (value) => appActions.setCustomAtaSettings({ [field]: value }),
        },
      },
    },
    routed: false,
    notes: 'Saved to the server through the existing debounced ATA call.',
  });

const customGoalTempo = defineSetting<boolean>({
  key: 'customGoalTempo',
  label: 'Custom goal tempo',
  valueType: { kind: 'boolean' },
  defaultValue: false,
  parse: booleanOr(false),
  scope: 'mode',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: ['sight_reading_tempo'],
  stores: [
    {
      layer: 'mode',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.ataSettings.usingCustomGoalTempo',
    },
  ],
  bindings: {
    mode: {
      sight_reading: {
        select: (state) => state.mainAppReducer?.ataSettings?.usingCustomGoalTempo,
        write: (value) => appActions.setUsingCustomGoalTempo(value),
      },
    },
  },
  routed: false,
});

const goalTempo = defineSetting<number>({
  key: 'goalTempo',
  label: 'Goal tempo',
  valueType: { kind: 'number', min: 1, step: 1 },
  defaultValue: 150,
  parse: positiveWholeNumberOr(150),
  scope: 'instance',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: ['sight_reading_tempo'],
  stores: [
    {
      layer: 'instance',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.ataSettings.goalTemposByTier[<tier>]',
    },
  ],
  routed: false,
  notes: 'One value per tier (the instance). Instance stores are not bound in this first model.',
});

const tempo = defineSetting<number>({
  key: 'tempo',
  label: 'Tempo',
  valueType: { kind: 'number', min: 1, step: 1 },
  defaultValue: 72,
  parse: positiveWholeNumberOr(72),
  scope: 'instance',
  modes: ['sight_reading'],
  precedence: 'feel',
  surfaces: ['sight_reading_tempo'],
  stores: [
    {
      layer: 'instance',
      mode: 'sight_reading',
      kind: 'redux_persist',
      location: 'mainAppReducer.tempoValues[<level>][<tier>]',
    },
  ],
  routed: false,
  notes:
    "The tempo a tier starts at. The tier's own tempo is the authored value under it. Instance stores are not bound in this first model.",
});

export const SETTINGS_REGISTRY = {
  keyboardSound,
  metronomeSound,
  accentDownbeats,
  preferredInput,
  pianoVolume: channelVolume('piano', 'Piano'),
  pianoMuted: channelMuted('piano', 'Piano'),
  metronomeVolume: channelVolume('metronome', 'Metronome'),
  metronomeMuted: channelMuted('metronome', 'Metronome'),
  narrationVolume: channelVolume('narration', 'Narration'),
  narrationMuted: channelMuted('narration', 'Narration'),
  sfxVolume: channelVolume('sfx', 'Sound effects'),
  sfxMuted: channelMuted('sfx', 'Sound effects'),
  scoreTheme,
  colorblindMode,
  keySignatureDisplay,
  scaleFingerNumbers,
  cursor,
  zoom,
  fingerNumbers,
  transpose,
  scaleMode,
  repertoirePlaybackMode,
  hands,
  perfectPhrase,
  perfectPhraseCount,
  autoTempo,
  customAutoTempo,
  autoTempoSpeedUpAt: ataNumber(
    'autoTempoSpeedUpAt',
    'Increase Tempo Threshold',
    'customIncThreshold',
    'custom_inc_threshold',
    { min: 80, max: 100, fallback: 95 },
  ),
  autoTempoSpeedUpBy: ataNumber(
    'autoTempoSpeedUpBy',
    'Increase Tempo Amount',
    'customIncAmt',
    'custom_inc_amt',
    { min: 1, max: 20, fallback: 5 },
  ),
  autoTempoSlowDownAt: ataNumber(
    'autoTempoSlowDownAt',
    'Decrease Tempo Threshold',
    'customDecThreshold',
    'custom_dec_threshold',
    { min: 60, max: 100, fallback: 80 },
  ),
  autoTempoSlowDownBy: ataNumber(
    'autoTempoSlowDownBy',
    'Decrease Tempo Amount',
    'customDecAmt',
    'custom_dec_amt',
    { min: 1, max: 20, fallback: 10 },
  ),
  customGoalTempo,
  goalTempo,
  tempo,
} as const;

export type SettingsRegistry = typeof SETTINGS_REGISTRY;
export type SettingKey = keyof SettingsRegistry;
export type SettingValue<K extends SettingKey> =
  SettingsRegistry[K] extends SettingDefinition<infer T> ? T : never;

export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY) as SettingKey[];

export const getSettingDefinition = <K extends SettingKey>(
  key: K,
): SettingDefinition<SettingValue<K>> => {
  const definition = SETTINGS_REGISTRY[key] as SettingDefinition<SettingValue<K>> | undefined;
  if (!definition) throw new Error(`Unknown setting: ${String(key)}`);
  return definition;
};
